import {
  computeWorstCaseRanking,
  WORST_CASE_SETTINGS_KEYS,
  DEFAULT_WEIGHTS,
  DEFAULT_SOLUBILITY,
  DEFAULT_CLEANABILITY,
  DEFAULT_PDE_RATINGS,
  DEFAULT_DOSE_RATINGS,
  DEFAULT_PRIORITY_ORDER,
  parseJsonSetting,
  parseJsonObject
} from './worstCaseRanking';

export const buildCleaningValidationCoverage = ({
  trains = [], apis = [], products = [], plans = [], trainMembersById = {},
  manualSoilingEntries = [], rankingConfig = {}, results = []
}) => {
  const ranking = computeWorstCaseRanking({
    trains,
    apis,
    products,
    weights: parseJsonSetting(rankingConfig[WORST_CASE_SETTINGS_KEYS.weights], DEFAULT_WEIGHTS),
    solubilityRatings: parseJsonSetting(rankingConfig[WORST_CASE_SETTINGS_KEYS.solubility], DEFAULT_SOLUBILITY),
    cleanabilityRatings: parseJsonSetting(rankingConfig[WORST_CASE_SETTINGS_KEYS.cleanability], DEFAULT_CLEANABILITY),
    pdeRatings: parseJsonSetting(rankingConfig[WORST_CASE_SETTINGS_KEYS.pde], DEFAULT_PDE_RATINGS),
    doseRatings: parseJsonSetting(rankingConfig[WORST_CASE_SETTINGS_KEYS.dose], DEFAULT_DOSE_RATINGS),
    scoringMethod: rankingConfig[WORST_CASE_SETTINGS_KEYS.scoringMethod] === 'priority' ? 'priority' : 'weighted',
    priorityOrder: parseJsonSetting(rankingConfig[WORST_CASE_SETTINGS_KEYS.priorityOrder], DEFAULT_PRIORITY_ORDER),
    manualOverrides: parseJsonObject(rankingConfig[WORST_CASE_SETTINGS_KEYS.manualOverrides])
  });

  return trains.map((train) => {
    const trainId = String(train.train_id);
    const members = trainMembersById[trainId] || [];
    // The ranking result is sorted by risk. Use the highest-ranked candidate for this train.
    const worstCase = ranking.find((row) => String(row.train_id) === trainId) || null;
    const allCvPlans = plans.filter((plan) => String(plan.train_id) === trainId
      && String(plan.study_type || '').toUpperCase() === 'CV');
    const approvedCvPlans = allCvPlans.filter((plan) => String(plan.status || '').toLowerCase() === 'approved');
    const worstCaseCvPlans = worstCase
      ? approvedCvPlans.filter((plan) => String(plan.previous_api_id || '') === String(worstCase.api_id || ''))
      : [];

    const missingEquipment = members.filter((member) => {
      const memberId = String(member.train_member_id || member.member_id || '');
      const equipmentId = String(member.equipment_id || '');
      const parts = Array.isArray(member.equipment_parts) ? member.equipment_parts : [];
      const requiredParts = parts.filter((part) => part.part_id);
      const hasLocation = (plan, part) => (plan.locations || []).some((location) => (
        part
          ? String(location.equipment_part_id) === String(part.part_id)
            && (!location.train_member_id || String(location.train_member_id) === memberId)
          : String(location.train_member_id || '') === memberId || String(location.equipment_id || '') === equipmentId
      ));
      return !worstCaseCvPlans.some((plan) => requiredParts.length
        ? requiredParts.every((part) => hasLocation(plan, part))
        : hasLocation(plan, null));
    });

    const soilingForTrain = manualSoilingEntries.filter((entry) => String(entry.train_id) === trainId);

    // Every location on the train's worst-case-matched, approved CV plan(s) — the set this
    // train is actually relying on for cleaning validation evidence.
    const coveredLocations = worstCaseCvPlans.flatMap((plan) => plan.locations || []);
    const locationsMissingLimits = coveredLocations.filter((location) => {
      const criteria = Array.isArray(location.acceptance_criteria) ? location.acceptance_criteria : [];
      return !criteria.some((ac) => ac.limit_value !== null && ac.limit_value !== undefined && ac.limit_value !== '');
    });
    const locationsMissingResults = coveredLocations.filter((location) => !results.some((result) => (
      String(result.location_id) === String(location.location_id)
      && worstCaseCvPlans.some((plan) => String(plan.plan_id) === String(result.plan_id))
    )));

    const gaps = [];
    if (!worstCase || worstCase.is_provisional) gaps.push('Approved worst case required');
    if (!approvedCvPlans.length) gaps.push('Approved CV sampling plan required');
    else if (worstCase && !worstCaseCvPlans.length) gaps.push('CV plan must use this train’s worst-case API');
    if (worstCaseCvPlans.length && missingEquipment.length) gaps.push(`${missingEquipment.length} equipment item(s) missing CV locations`);
    if (worstCaseCvPlans.length && locationsMissingLimits.length) gaps.push(`${locationsMissingLimits.length} location(s) missing an approved acceptance limit`);
    if (worstCaseCvPlans.length && coveredLocations.length && locationsMissingResults.length) gaps.push(`${locationsMissingResults.length} location(s) with no recorded results yet`);
    if (!members.length) gaps.push('Link equipment to this train');

    return {
      train, members, worstCase, allCvPlans, approvedCvPlans, worstCaseCvPlans, missingEquipment, soilingForTrain, gaps,
      locationsMissingLimits, locationsMissingResults, coveredLocations,
    };
  });
};
